import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import Dropdown from "../../components/inputFields/DropDown";
import ErrorBanner from "../../components/dashboard/ErrorBanner";
import { OrderFilters, PeriodFilter, NO_RANGE, formatInr, formatNum } from "./ProductSales";
import { getProductSales, getProductSalesDetail, getProductSalesDistricts } from "../../services/productSales";
import DistrictSalesMap from "../../components/Maps/DistrictSalesMap";
import { getFyOptions, ALL_ORDER_TYPES, filterParams } from "./productSalesOptions";

const LABEL = "text-[11px] font-semibold uppercase tracking-wider text-gray-500";
const TH = "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-600 bg-gray-100 whitespace-nowrap";
const TD = "px-3 py-2.5 text-gray-700 whitespace-nowrap border-t border-gray-100";
const FY_OPTIONS = getFyOptions();

const ProductSalesDetail = () => {
  const { sku } = useParams();
  const navigate = useNavigate();
  const [fy, setFy] = useState(FY_OPTIONS[0].value);
  const [month, setMonth] = useState("all");
  const [dateRange, setDateRange] = useState(NO_RANGE);
  const [orderTypes, setOrderTypes] = useState(ALL_ORDER_TYPES);
  const [statuses, setStatuses] = useState(["DELIVERED"]);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [districts, setDistricts] = useState(null);
  const [districtsLoading, setDistrictsLoading] = useState(false);

  const params = filterParams({ fy, month, dateRange, orderTypes, statuses });
  const paramsKey = JSON.stringify(params);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    setNotFound(false);
    getProductSalesDetail(sku, JSON.parse(paramsKey))
      .then((res) => setData(res.data))
      .catch((err) => (err.response?.status === 404 ? setNotFound(true) : setError(true)))
      .finally(() => setLoading(false));
  }, [sku, paramsKey]);

  useEffect(() => {
    load();
  }, [load]);

  // District map data: same filters as the page
  useEffect(() => {
    setDistrictsLoading(true);
    getProductSalesDistricts(sku, JSON.parse(paramsKey))
      .then((res) => setDistricts(res.data))
      .catch(() => setDistricts(null))
      .finally(() => setDistrictsLoading(false));
  }, [sku, paramsKey]);

  // Product switcher: products sold in this period, most sold first
  const rangeKey = dateRange.start && dateRange.end ? `${dateRange.start}|${dateRange.end}` : "";
  useEffect(() => {
    const [start_date, end_date] = rangeKey.split("|");
    getProductSales(rangeKey ? { start_date, end_date, page_size: 500 } : { fy, page_size: 500 })
      .then((res) =>
        setProductOptions(
          res.data.results.map((p) => ({ value: p.sku, label: [p.name, p.variant].filter(Boolean).join(" — ") }))
        )
      )
      .catch(() => setProductOptions([]));
  }, [fy, rangeKey]);

  if (notFound) {
    return (
      <div className="p-5 space-y-2">
        <p className="text-sm text-gray-600">Product not found.</p>
        <Link to="/product-sales" className="text-sm font-semibold text-green-700">
          Back to Product Sales
        </Link>
      </div>
    );
  }

  const product = data?.product;
  const rows = data?.months || [];
  const options =
    product && !productOptions.some((o) => o.value === product.sku)
      ? [{ value: product.sku, label: [product.name, product.variant].filter(Boolean).join(" — ") }, ...productOptions]
      : productOptions;

  const cards = data
    ? [
        { label: "Quantity sold", value: formatNum(data.total_units), sub: data.fy && month !== "all" ? `${month}, ${data.period}` : data.period },
        { label: "Revenue", value: formatInr(data.total_revenue), sub: "From this product only" },
        {
          label: "Avg selling price",
          value: data.total_units ? formatInr(data.avg_price, 2) : "—",
          sub: product.mrp ? `MRP ${formatInr(product.mrp)}` : "MRP not set",
        },
        { label: "Orders", value: formatNum(data.total_orders), sub: "Orders with this product" },
      ]
    : [];

  return (
    <div className="p-3 md:p-5 space-y-4 bg-gray-50 min-h-screen">
      <p className="text-xs text-gray-500">
        <Link to="/product-sales" className="font-medium text-green-700 hover:text-green-800">
          Product Sales
        </Link>{" "}
        / {product?.name || sku}
      </p>

      {/* Header + filters */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-visible">
        <div className="px-4 py-4 sm:px-5 space-y-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900 tracking-tight">{product?.name || sku}</h1>
              {loading && <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-blue-500" title="Loading" />}
            </div>
            {product && (
              <p className="mt-1 text-xs text-gray-500">
                {[product.variant, `SKU ${product.sku}`, product.domain, product.mrp && `MRP ${formatInr(product.mrp)}`]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <p className={`${LABEL} mb-1`}>Product</p>
              <Dropdown
                options={options}
                value={sku}
                onChange={(v) => navigate(`/product-sales/${encodeURIComponent(v)}`)}
                needSearch
                compact
              />
            </div>
            <PeriodFilter
              fy={fy}
              setFy={setFy}
              month={month}
              setMonth={setMonth}
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          </div>
        </div>
        <OrderFilters orderTypes={orderTypes} setOrderTypes={setOrderTypes} statuses={statuses} setStatuses={setStatuses} />
      </div>

      {error && <ErrorBanner onRetry={load} />}

      {data && (
        <div className={`space-y-4 transition-opacity ${loading ? "opacity-50" : ""}`}>
          {/* Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map((c) => (
              <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-3.5">
                <p className="text-xs font-medium text-gray-500">{c.label}</p>
                <p className="mt-2 text-lg font-bold text-gray-900 leading-tight">{c.value}</p>
                <p className="mt-1 text-xs text-gray-500">{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Monthly chart */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Monthly quantity sold</h2>
            <p className="mt-0.5 mb-3 text-xs text-gray-500">Hover a bar to see revenue.</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
                  formatter={(v, name, item) => [`${formatNum(v)} units · ${formatInr(item.payload.revenue)}`, "Sold"]}
                />
                <Bar dataKey="units" fill="#2FB95D" radius={[4, 4, 0, 0]} maxBarSize={56} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Sales by district */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Sales by district</h2>
            <p className="mt-0.5 mb-2 text-xs text-gray-500">
              Placed by the shipping address pincode (or the typed district when there is no pincode). Same filters as above.
            </p>
            <DistrictSalesMap data={districts} loading={districtsLoading} />
          </div>

          {/* Month by month */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h2 className="mb-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Month by month</h2>
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={`${TH} text-left`}>Month</th>
                    <th className={`${TH} text-right`}>Qty sold</th>
                    <th className={`${TH} text-right`}>Revenue</th>
                    <th className={`${TH} text-right`}>Avg price</th>
                    <th className={`${TH} text-right`}>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.month}-${r.year}`}>
                      <td className={`${TD} font-semibold text-gray-900`}>
                        {r.month} {r.year}
                      </td>
                      <td className={`${TD} text-right`}>{formatNum(r.units)}</td>
                      <td className={`${TD} text-right`}>{formatInr(r.revenue)}</td>
                      <td className={`${TD} text-right`}>{r.units ? formatInr(r.avg_price, 2) : "—"}</td>
                      <td className={`${TD} text-right`}>{formatNum(r.orders)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSalesDetail;
